import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { hcApi, hcError } from './api';
import { HcLayout } from './HcLayout';
import { Legend, MonthGrid, ItemRow, parseDay, longDate } from './HcCalendarBits';

const UPCOMING_LIMIT = 15;

export default function HcCalendarPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [month, setMonth] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    hcApi.get('/calendar').then(({ data: d }) => {
      setData(d);
      setSelected(d.today);
      const t = parseDay(d.today);
      setMonth(new Date(t.getFullYear(), t.getMonth(), 1));
    }).catch((e) => setError(hcError(e)));
  }, []);

  const byDay = useMemo(() => (data?.items || []).reduce((acc, i) => { (acc[i.date] = acc[i.date] || []).push(i); return acc; }, {}), [data]);
  const upcomingAll = (data?.items || []).filter((i) => i.date >= data.today || (i.type === 'deadline' && i.overdue));
  const lessonIds = new Set(upcomingAll.filter((i) => i.type === 'lesson').slice(0, UPCOMING_LIMIT).map((i) => i.id));
  const upcoming = upcomingAll.filter((i) => i.type !== 'lesson' || lessonIds.has(i.id));
  const open = (item) => navigate(item.lesson_id ? `/helpconnect/courses/${item.course_id}/lessons/${item.lesson_id}` : `/helpconnect/courses/${item.course_id}`);
  const dayItems = (selected && byDay[selected]) || [];

  return (
    <HcLayout>
      <div className="max-w-7xl hc-fade-up" data-testid="hc-calendar-page">
        <h1 className="text-4xl sm:text-5xl font-extrabold text-[#363636]">Learning Calendar</h1>
        <p className="text-gray-500 mt-2">Your lessons, course deadlines and appointments in one place. Lesson dates are suggestions to help you finish on time.</p>
        <div className="mt-4"><Legend /></div>
        {!data && !error && <Loader2 className="w-8 h-8 animate-spin text-[#922A8C] mt-6" />}
        {error && <p className="text-red-600 mt-6" data-testid="hc-calendar-error">{error}</p>}
        {data && month && (
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 mt-6">
            <div className="space-y-4 min-w-0">
              <MonthGrid month={month} setMonth={setMonth} byDay={byDay} today={data.today} selected={selected} onSelect={setSelected} />
              <div data-testid="hc-calendar-selected-day">
                <h2 className="text-base md:text-lg font-bold text-[#363636] mb-2">{selected ? longDate(selected) : ''}</h2>
                {dayItems.length === 0
                  ? <p className="text-sm text-gray-500" data-testid="hc-calendar-day-empty">Nothing scheduled for this day.</p>
                  : <div className="space-y-2">{dayItems.map((i) => <ItemRow key={i.id} item={i} onOpen={open} />)}</div>}
              </div>
            </div>
            <aside className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 self-start min-w-0" data-testid="hc-calendar-upcoming">
              <h2 className="text-base md:text-lg font-bold text-[#363636] mb-3">Coming Up</h2>
              {upcoming.length === 0
                ? <p className="text-sm text-gray-500" data-testid="hc-calendar-upcoming-empty">You're all caught up. Nothing coming up right now.</p>
                : <div className="space-y-2">{upcoming.map((i) => <ItemRow key={i.id} item={i} onOpen={open} showDate />)}</div>}
            </aside>
          </div>
        )}
      </div>
    </HcLayout>
  );
}
